import React from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './react_dates_overrides.css';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import { useTheme } from '@material-ui/core/styles';

function DateRangePickerOmni({ startDate, endDate, onDateChange, defaultNull, color = true, style }) {
    const [focus, setFocus] = React.useState(null);
    const theme = useTheme();

    // Local state lets the picker visually reflect an in-progress (single-date) selection
    // without propagating an invalid/null endDate to consumers, which would trigger API calls.
    const [localStart, setLocalStart] = React.useState(startDate);
    const [localEnd, setLocalEnd] = React.useState(endDate);

    React.useEffect(() => {
        setLocalStart(startDate);
        setLocalEnd(endDate);
    }, [startDate, endDate]);

    const handleOnDateChange = ({ startDate: newStart, endDate: newEnd }) => {
        setLocalStart(newStart);
        setLocalEnd(newEnd);

        if (!onDateChange) return;

        // Both cleared (user hit the clear button): reset to today, or emit nulls in defaultNull mode.
        if (!newStart && !newEnd) {
            if (defaultNull) {
                onDateChange({ startDate: null, endDate: null });
            } else {
                onDateChange({ startDate: moment.utc().startOf('day'), endDate: moment.utc().endOf('day') });
            }
            return;
        }

        // Intermediate state — only one date picked. Keep the visual selection but don't
        // notify the parent yet, so no API call fires with a half-set range.
        if (!newStart || !newEnd) return;

        onDateChange({
            startDate: moment(newStart).startOf('day'),
            endDate: moment(newEnd).endOf('day')
        });
    };

    const orientation = window.matchMedia('(max-width: 700px)').matches ? 'vertical' : 'horizontal';

    return (
        <div style={style}>
            {/* Theme rules are global (not instance-scoped) so they still apply to the
                calendar popup after `appendToBody` portals it out to <body>. Only mounted
                while this picker is on screen, so it doesn't affect other pages' pickers. */}
            <style>{`
                .DateRangePicker_picker {
                    z-index: 99999 !important;
                    background-color: ${theme.palette.background.default} !important;
                    border: 1px solid ${theme.palette.divider || 'rgba(255, 255, 255, 0.12)'} !important;
                }
                
                /* Remove all white backgrounds */
                .DayPicker,
                .DayPicker__horizontal,
                .DayPicker_transitionContainer,
                .DayPicker_transitionContainer__horizontal {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                /* Calendar background */
                .CalendarMonth {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                .CalendarMonthGrid {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                .CalendarMonth_table {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                /* Month title text */
                .CalendarMonth_caption {
                    color: ${theme.palette.text.primary} !important;
                    background-color: ${theme.palette.background.default} !important;
                }
                
                /* Day of week headers (Su, Mo, Tu, etc.) */
                .DayPicker_weekHeader {
                    color: ${theme.palette.text.primary} !important;
                    background-color: ${theme.palette.background.default} !important;
                }
                
                .DayPicker_weekHeader_ul {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                .DayPicker_weekHeader_li {
                    background-color: ${theme.palette.background.default} !important;
                }
                
                .DayPicker_weekHeader_li small {
                    color: ${theme.palette.text.primary} !important;
                }
                
                /* Day numbers */
                .CalendarDay {
                    background-color: ${theme.palette.background.default} !important;
                    color: ${theme.palette.text.primary} !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                .CalendarDay__default {
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                /* Hovered days */
                .CalendarDay__default:hover {
                    background-color: ${theme.palette.action?.hover || 'rgba(255, 255, 255, 0.08)'} !important;
                    color: ${theme.palette.text.primary} !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                /* Selected days */
                .CalendarDay__selected,
                .CalendarDay__selected:active,
                .CalendarDay__selected:hover {
                    background-color: #008489 !important;
                    color: #ffffff !important;
                    border: 1px solid #008489 !important;
                }
                
                /* Selected span (range between start and end) */
                .CalendarDay__selected_span {
                    background-color: rgba(0, 132, 137, 0.3) !important;
                    color: ${theme.palette.text.primary} !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                .CalendarDay__selected_span:hover {
                    background-color: rgba(0, 132, 137, 0.5) !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                /* Hovered span */
                .CalendarDay__hovered_span,
                .CalendarDay__hovered_span:hover {
                    background-color: rgba(0, 132, 137, 0.2) !important;
                    color: ${theme.palette.text.primary} !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                /* Navigation arrows */
                .DayPickerNavigation_button {
                    background-color: ${theme.palette.background.default} !important;
                    border: 1px solid ${theme.palette.divider || 'rgba(255, 255, 255, 0.12)'} !important;
                }
                
                .DayPickerNavigation_button:hover {
                    background-color: ${theme.palette.action?.hover || 'rgba(255, 255, 255, 0.08)'} !important;
                }
                
                .DayPickerNavigation_svg__horizontal {
                    fill: ${theme.palette.text.primary} !important;
                }
                
                /* Input fields */
                .DateInput {
                    background-color: transparent !important;
                }
                
                .DateInput_input {
                    background-color: transparent !important;
                    color: ${theme.palette.text.primary} !important;
                    font-weight: 500 !important;
                }
                
                .DateInput_input::placeholder {
                    color: ${theme.palette.text.secondary} !important;
                    opacity: 0.7 !important;
                }
                
                /* Focused input */
                .DateInput_input__focused {
                    border-bottom: 2px solid ${theme.palette.text.primary} !important;
                }
                
                /* Input container - MAKE TRANSPARENT */
                .DateRangePickerInput {
                    background-color: transparent !important;
                    border: none !important;
                }
                
                .DateRangePickerInput__withBorder {
                    background-color: transparent !important;
                    border: none !important;
                }
                
                .DateRangePickerInput__showClearDates {
                    background-color: transparent !important;
                }
                
                /* Arrow between inputs */
                .DateRangePickerInput_arrow {
                    color: ${theme.palette.text.primary} !important;
                }
                
                .DateRangePickerInput_arrow_svg {
                    fill: ${theme.palette.text.primary} !important;
                }
                
                /* Clear dates button */
                .DateRangePickerInput_clearDates {
                    background-color: transparent !important;
                }
                
                .DateRangePickerInput_clearDates_svg {
                    fill: ${theme.palette.text.primary} !important;
                }
                
                .DateRangePickerInput_clearDates:hover {
                    background-color: ${theme.palette.action?.hover || 'rgba(255, 255, 255, 0.08)'} !important;
                }
                
                /* Calendar icon */
                .DateRangePickerInput_calendarIcon {
                    background-color: transparent !important;
                }
                
                .DateRangePickerInput_calendarIcon_svg {
                    fill: ${theme.palette.text.primary} !important;
                }
                
                /* Blocked/disabled days */
                .CalendarDay__blocked_out_of_range {
                    background-color: ${theme.palette.background.default} !important;
                    color: ${theme.palette.text.disabled} !important;
                    border: 1px solid ${theme.palette.type === 'light' ? '#e4e7eb' : 'rgba(255, 255, 255, 0.2)'} !important;
                }
                
                /* Remove any remaining white gaps */
                .CalendarMonth_caption,
                .CalendarMonth_table tbody {
                    background-color: ${theme.palette.background.default} !important;
                }

                /* Info panel/dialog (appears when clicking ?) */
                .DayPickerKeyboardShortcuts_panel {
                    background-color: ${theme.palette.background.paper} !important;
                }
                
                .DayPickerKeyboardShortcuts_title {
                    color: ${theme.palette.text.primary} !important;
                }
                
                .DayPickerKeyboardShortcuts_list {
                    background-color: ${theme.palette.background.paper} !important;
                }
                
                .KeyboardShortcutRow {
                    color: ${theme.palette.text.primary} !important;
                }
            `}</style>
            <DateRangePicker
                orientation={orientation}
                appendToBody
                isOutsideRange={() => false}
                startDate={localStart}
                endDate={localEnd}
                onDatesChange={handleOnDateChange}
                startDatePlaceholderText="Start Date"
                endDatePlaceholderText="End Date"
                numberOfMonths={2}
                displayFormat="MMM D, YYYY"
                showClearDates={true}
                focusedInput={focus}
                onFocusChange={setFocus}
                startDateId="globalStartDate"
                endDateId="globalEndDate"
                minimumNights={0}
                small
            />
        </div>
    );
}

export default DateRangePickerOmni;